import videojs from 'video.js';
import { localStorageAvailable } from '../../util';

const _defaultPositionValue = 0.0;
const _minimumPositionValue = 0.0;
const _maximumPositionValue = 10000 * 24.0 * 60.0 * 60.0;
const _noBookmarkZoneSize = 3;
const _maximumBookmarks = 10;
const _bookmarksKeyName = "bookmarks";
const _lastStartTime = new WeakMap();
const _presentationId = new WeakMap();
const _presentationDuration = new WeakMap();
const _isLive = new WeakMap();
const _clockSkew = new WeakMap();


class BookmarkStorePlugin extends videojs.getPlugin('plugin') {
    
    constructor(player, options) {
        super(player, options);

        player.ready(() => {
            this.on(player, 'ended', this.#handlePresentationEnded);
            if (localStorageAvailable()) {
                this.log('Local storage is available.');
            } else {
                this.log('Local storage is unavailable.');
            }
        });
    }

    get isLive() {
        return _isLive.get(this) ?? false;
    }

    set isLive(isLive) {
        _isLive.set(this, isLive);
    }

    get clockSkew() {
        return _clockSkew.get(this) ?? 0;
    }

    set clockSkew(clockSkew) {
        _clockSkew.set(this, clockSkew);
    }

    get lastStartTime() {
        return _lastStartTime.get(this) ?? null;
    }

    set lastStartTime(lastStartTime) {
        _lastStartTime.set(this, lastStartTime);
    }

    get presentationId() {
        return _presentationId.get(this);
    }

    set presentationId(presentationId) {
        _presentationId.set(this, presentationId);
    }

    get presentationDuration() {
        return _presentationDuration.get(this);
    }

    set presentationDuration(presentationDuration) {
        _presentationDuration.set(this, presentationDuration);
    }

    save(position) {
        //bail if we don't have access to local storage, or is live, or _lastStartTime is null
        if (!localStorageAvailable() || this.isLive || this.lastStartTime === null) {
            return;
        }

        //bail if position = _lastStartTime
        var durationViewed = (position - this.lastStartTime);
        if (durationViewed === 0) {
            return;
        }

        //grab bookmarks from storage
        var bookmarks = JSON.parse(localStorage.getItem(_bookmarksKeyName));
        var bookmarksOriginalString = JSON.stringify(bookmarks);

        if (bookmarks === null) { bookmarks = []; }

        //delete existing bookmark
        for (var i = 0; i < bookmarks.length; i++) {
            if (bookmarks[i].presentationId === this.presentationId) {
                bookmarks.splice(i, 1);
            }
        }

        //add bookmark (if valid position)
        if (position > _minimumPositionValue && position < _maximumPositionValue && Math.abs(this.presentationDuration - position) > _noBookmarkZoneSize) {
            var _date = new Date();
            var _timestamp = Date.UTC(_date.getFullYear(), _date.getMonth(), _date.getDate(), _date.getHours(), _date.getMinutes(), _date.getSeconds(), _date.getMilliseconds());
            _timestamp = _timestamp - this.clockSkew; // adjust for server skew
            bookmarks.push({ presentationId: this.presentationId, position: position, savedAt: _timestamp / 1000 });
        }

        //truncate
        bookmarks.sort(this.compareSavedAt);
        while (bookmarks.length > _maximumBookmarks) {
            bookmarks.shift();
        }

        //store bookmarks (if any change) in localstorage
        if (JSON.stringify(bookmarks) !== bookmarksOriginalString) {
            if (bookmarks.length === 0) {
                localStorage.removeItem(_bookmarksKeyName);
            } else {
                localStorage.setItem(_bookmarksKeyName, JSON.stringify(bookmarks));
            }
        }

        this.lastStartTime = position;
        return bookmarks.length;

    };

    retrieve() {
        if (!localStorageAvailable()) {
            return _defaultPositionValue;
        }

        var bookmarks = JSON.parse(localStorage.getItem(_bookmarksKeyName));

        var bookmark = { position: 0, savedAt: 0 };

        if (bookmarks !== null) {
            for (var i = 0; i < bookmarks.length; i++) {
                if (bookmarks[i].presentationId === this.presentationId) {
                    bookmark.position = Math.min(Math.max(bookmarks[i].position, _minimumPositionValue), _maximumPositionValue);
                    bookmark.savedAt = bookmarks[i].savedAt;
                    return bookmark;
                }
            }
        }

        return bookmark;

    };

    #handlePresentationEnded() {
        this.save(this.presentationDuration);
    }

    compareSavedAt(a, b) {
        if (a.savedAt < b.savedAt)
            return -1;
        if (a.savedAt > b.savedAt)
            return 1;
        return 0;
    };

    watchViewTracking(viewTracking, playbackTicketId) {
        var self = this;
        viewTracking.addHandler({
            start: function (start) {
                self.start(start);
            },
            end: function (start, end, important) {
                self.save(end, playbackTicketId);
            }
        });
    };
}

export default BookmarkStorePlugin;
